/**
 * Created by mzhao on 01/11/2023.
 * Description:
 * AdminRelationships Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { AppState } from '../../../../redux';
import { filter, take } from 'rxjs/operators';
import { validRelationshipTypeSelector } from '../../../../redux/reducers/valid-relationship-type.reducer';
import { DEFAULT_PAGE_SIZE, GridUtility } from '../../grid-utility';
import { ValidRelationshipType } from '../../../../tamalelibs/models/valid-relationship-type.model';
import { ResearchView } from '../../../../constants/business.constants';
import { DeleteRelationshipType, RelationshipTypeActionTypes } from '../../../../redux/actions/relationship-type.actions';
import { AdminPageActionsComponent } from '../../admin-page-actions/admin-page-actions.component';
import { adminPagesConstants } from '../../admin-page-setting.constants';
import { AdminPageFeedback, AdminPageFeedbackTypes, AdminPageSetting } from '../../admin-page-setting.model';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../../widgets/tam-context-menu/tam-context-menu.model';
import { EditRelationshipService } from '../../../edit-relationship-dialog/edit-relationship-dialog.service';
import { EditRelationshipDialogOpenOptions } from '../../../edit-relationship-dialog/edit-relationship-dialog.model';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';

@Component({
    selector: 'tam-admin-relationships',
    templateUrl: './admin-relationships.component.html',
    styleUrls: ['./admin-relationships.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminRelationshipsComponent implements OnInit, OnDestroy {

    /**
    * an array of can not edit.
    */
    canNotEditList = ['is in team', 'Other', 'Topic Member'];

    /**
    * an array of can not delete.
    */
    canNotDeleteList = ['is in team', 'Topic Member'];

    /**
    * an array of column definitions.
    */
    columnDefs: Array<ColDef> = [];

    /**
    * context menu to edit relationship type.
    */
    contextMenuConfig: ContextMenuConfig;

    /**
     * The option settings for the inner grid.
     */
    gridOptions: GridOptions;

    /**
     * a flag indicating whether the spinner should be visible.
     */
    isReloading = false;

    /**
     * default to hide the no data indicator.
     */
    noDataStyle = 'none';

    /**
     * old relationship type need to changed.
     */
    oldRelationshipTypeId: string;

    /**
     * old relationship type need to changed.
     */
    oldRelationshipTypeName: string;

    /**
     * relationship page setting.
     */
    relationshipPage: AdminPageSetting;

    /**
     * relationship type id.
     */
    relationshipTypeId: string;

    /**
     * the data source of the grid.
     */
    rowData: Array<ValidRelationshipType> = [];

    relationshipType = 'relationshipType';

    reciprocalRelationshipType = 'reciprocalRelationshipType';

    /**
     * the Grid API reference
     */
    private _gridApi: GridApi;
    private _columnApi;

    /**
     * the subscription collection persisting for unsubscribing them on destroying
     */
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _alert: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _editRelationshipService: EditRelationshipService,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _toast: ToastService,
    ) { }

    ngOnInit() {
        this._store.dispatch({
            type: RelationshipTypeActionTypes.GETVALIDRELATIONSHIP
        });
        this._destroySubscriptions.push(
            this._store.pipe(
                select(validRelationshipTypeSelector),
                filter(res => res && res.length > 0),
            ).subscribe(res => {
                this.rowData = res;
                this.rowData.forEach(item => {
                    item.editable = false;
                });
                // Due to when switch router,grid header was loss.reload grid config can fix this issue.
                this._loadGridConfig();
                this._changeDetectorRef.detectChanges();
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
        );
        this.relationshipPage = adminPagesConstants.relationships;
        this._destroySubscriptions.push(this.relationshipPage.feedbackSubject$.subscribe(feedback => this._handleGridFeedback(feedback)));
        this._loadGridConfig();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    editRelationship() {
        const editRelationshipDialogOpenOptions = new EditRelationshipDialogOpenOptions();
        editRelationshipDialogOpenOptions.relationshipTypeId = this.relationshipTypeId;
        editRelationshipDialogOpenOptions.oldRelationshipTypeId = this.oldRelationshipTypeId;
        editRelationshipDialogOpenOptions.oldRelationshipTypeName = this.oldRelationshipTypeName;
        this._editRelationshipService.dialogOpen$.next(editRelationshipDialogOpenOptions);
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }


    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onColumnEverythingChanged(event) {
        if (event.source === 'contextMenu') {
            const sort = [
                {
                    colId: 'relationshipType',
                    sort: 'asc'
                }
            ];
            this._columnApi.setRowGroupColumns([]);
            this._gridApi.setFilterModel(null);
            this._columnApi.applyColumnState({ state: sort });
            this._changeDetectorRef.detectChanges();
        }
    }

    // grid column row group changed event
    onColumnRowGroupChanged(event) {
        const columns = event.columns.map((item, i) => ({
            colId: item.colId,
            rowGroupIndex: i
        }));
        this.columnDefs.forEach((column) => {
            const index = columns.findIndex(item => item.colId === column.colId);
            if (index !== -1) {
                column.rowGroupIndex = columns[index].rowGroupIndex;
                column.comparator = (valueA, valueB, nodeA, nodeB) => GridUtility.customColumnSortComparator(nodeA, nodeB, column);
            } else {
                delete column.rowGroupIndex;
            }
        });
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;
    }

    onContextMenu(event) {
        if (!this.canNotEditList.includes(event.value)) {
            if (event.column.colId === this.relationshipType || event.column.colId === this.reciprocalRelationshipType) {
                this.relationshipTypeId = event.column.colId === this.relationshipType ? event.data.relationshipType.id : event.data.reciprocalRelationshipType.id;
                this.oldRelationshipTypeId = event.column.colId === this.relationshipType ? event.data.relationshipType.id : event.data.reciprocalRelationshipType.id;
                this.oldRelationshipTypeName = event.column.colId === this.relationshipType ? event.data.relationshipType.name : event.data.reciprocalRelationshipType.name;
                this._changeDetectorRef.detectChanges();
                this.contextMenuConfig.show$.next(new ContextMenuShowAction(event.event, event.data));
            }
        }
    }

    onButtonClick() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_RELATIONSHIPS_EDITOR
        });
    }

    showToast(toastMsg, state) {
        if (NotificationStyles[state]) {
            this._toast.notify({
                message: toastMsg,
                style: state
            });
        } else {
            return;
        }
    }

    suppressDefaultContextMenu(event) {
        // agGrid bug, we need to handle this
        event.preventDefault();
        event.stopPropagation();
    }

    private _compareValue(columnDefs) {
        columnDefs.forEach(def => {
            def.comparator = function (valueA, valueB, nodeA, nodeB) {
                if (valueA && valueB && valueA.length > 0 && valueB.length > 0) {
                    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
                }
            };
        });
    }

    private _generateColDefs(): Array<ColDef> {
        return [
            {
                headerName: 'Relationship type',
                colId: 'relationshipType',
                valueGetter: 'data.relationshipType.name',
                minWidth: 80,
                width: 350,
                sortable: true,
                sort: 'asc',
                enableRowGroup: true,
                cellStyle: GridUtility.cellStyleFunc,
            },
            {
                headerName: 'Parent entity type',
                colId: 'parentEntityType',
                valueGetter: 'data.parentEntityType.name',
                minWidth: 50,
                width: 300,
                sortable: true,
                enableRowGroup: true,
                cellStyle: GridUtility.cellStyleFunc,
            },
            {
                headerName: 'Child entity type',
                colId: 'childEntityType',
                valueGetter: 'data.childEntityType.name',
                minWidth: 50,
                width: 300,
                sortable: true,
                enableRowGroup: true,
                cellStyle: GridUtility.cellStyleFunc,
            },
            {
                headerName: 'Reciprocal relationship type',
                colId: 'reciprocalRelationshipType',
                valueGetter: 'data.reciprocalRelationshipType.name',
                minWidth: 50,
                width: 300,
                sortable: true,
                enableRowGroup: true,
                cellStyle: GridUtility.cellStyleFunc,
            },
            {
                headerName: 'Delete',
                colId: 'delete',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                cellRenderer: AdminPageActionsComponent,
                cellRendererParams: { feedbackSubject$: () => adminPagesConstants.relationships.feedbackSubject$ },
                menuTabs: [],
                headerClass: 'delete-button',
            },
        ];
    }

    private _generateGridOpions(): GridOptions {
        return {
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: 'agSetColumnFilter',
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            cacheBlockSize: ResearchView.cacheBlockSize,
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            groupDisplayType: 'groupRows',
            paginationPageSize: DEFAULT_PAGE_SIZE,
            colResizeDefault: 'shift',
            rowSelection: 'single', // we only need to support single selection on contat list.
            rowGroupPanelShow: 'always',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            suppressRowTransform: true,
            suppressMultiSort: true,
            getRowHeight: () => 40,
        };
    }

    private _handleGridFeedback(feedback: AdminPageFeedback) {
        switch (feedback.type) {
            case AdminPageFeedbackTypes.EDIT:
                // this._handleEdit(feedback);
                break;
            case AdminPageFeedbackTypes.DELETE:
                this._handleDelete(feedback);
                break;
            default:
                break;
        }
    }

    // We won't do it here for now
    private _handleEdit(feedback: AdminPageFeedback) {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_RELATIONSHIPS_EDITOR,
            payload: feedback.payload.data
        });
    }

    private _handleDelete(feedback: AdminPageFeedback) {
        this._alert.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'), [StringLiteralsPipe.translate('general.sure_want_to_delete_this_relationship')], 'Delete', 'Cancel').pipe(
            take(1)
        ).subscribe(confirmation => {
            if (confirmation) {
                const relationshipTypeId = feedback.payload.data.relationshipType.id;
                const relationshipId = feedback.payload.data.id;
                const deleteAction = {
                    relationshipTypeId: relationshipTypeId,
                    relationshipId: relationshipId,
                };
                this._store.dispatch(new DeleteRelationshipType(deleteAction));
            }
        });
    }

    private _initContextMenu() {
        if (!this.contextMenuConfig) {
            this.contextMenuConfig = new ContextMenuConfig();
            this.contextMenuConfig.stateful = false;
            this.contextMenuConfig.forceRefresh = true;
            this.contextMenuConfig.items = [];
            this.contextMenuConfig.items.push({ id: '0', text: 'Edit relationship type', disabled: false });
            this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
        }
    }

    private _loadGridConfig() {
        // Before each load Grid Config, it is necessary to ensure that ContextMenu has been init
        this._initContextMenu();
        this.columnDefs = this._generateColDefs();
        this._compareValue(this.columnDefs);
        this.gridOptions = this._generateGridOpions();
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        switch (type) {
            case 0:
                this.editRelationship();
                break;
            default:
                break;
        }
    }

    private _onSubscribeActionMessage(action) {
        switch (action.type) {
            case RelationshipTypeActionTypes.CREATE_VALID_RELATIONSHIP_TYPE_SUCCESS:
                this.showToast(StringLiteralsPipe.translate('general.create_valid_relationship_successfully'), NotificationStyles.Success);
                this._slideSheetService.slideSheetActionSubject$.next({
                    type: SlideSheetActionTypes.CLOSE
                });
                break;
            case RelationshipTypeActionTypes.DELETE_RELATIONSHIP_TYPE_SUCCESS:
                this.showToast(StringLiteralsPipe.translate('general.delete_valid_relationship_successfully'), NotificationStyles.Success);
                break;
            case RelationshipTypeActionTypes.CREATE_VALID_RELATIONSHIP_TYPE_FAILED:
                this.showToast(action.payload, NotificationStyles.Error);
                break;
            case RelationshipTypeActionTypes.DELETE_RELATIONSHIP_TYPE_FAILED:
                this.showToast(action.payload, NotificationStyles.Error);
                break;
            default:
                break;
        }
    }
}
